import { Component, Element, h, Host, Prop } from '@stencil/core'

import Helpers from '../../../utils/helpers'
import { CssClassMap } from '../../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/footer?tab=designer).
 */
@Component({
  tag: 'br-footer-logo',
  styleUrl: 'footer-logo.scss',
  shadow: true,
})
export class FooterLogo {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrFooterLogoElement

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * Url da logo padrão.
   */
  @Prop({ reflect: true }) src: string = ''

  /**
   * Descrição da logo.
   */
  @Prop({ reflect: true }) description: string = 'logo'

  /**
   * Posição da logo.
   */
  @Prop({ reflect: true }) position: 'left' | 'center' | 'right' = 'left'

  /**
   * Define se a logo é de um parceiro.
   */
  @Prop({ reflect: true }) readonly isPartner: boolean = false

  /**
   * URL de destino quando a logo for clicada.
   * Se fornecido, a logo será envolvida em um link.
   */
  @Prop({ reflect: true }) href?: string

  /**
   * Target do link quando href é fornecido.
   */
  @Prop({ reflect: true }) target?: '_blank' | '_self' | '_parent' | '_top' = '_self'

  /**
   * Monta o mapa de classes CSS que serão aplicadas ao componente.
   * @returns Objeto com as classes CSS que serão aplicadas ao componente.
   */
  private getCssClassMap(): CssClassMap {
    return {
      'br-footer-logo': true,
      logo: true,
      'text-sm-left': this.position === 'left',
      'text-sm-center': this.position === 'center',
      'text-sm-right': this.position === 'right',
      partner: this.isPartner,
    }
  }

  /**
   * Monta o mapa de classes CSS que serão aplicadas ao host do componente.
   * @returns Objeto com as classes CSS que serão aplicadas ao host do componente.
   */
  private getRootCssClassMap(): CssClassMap {
    return {
      partner: this.isPartner,
    }
  }

  /**
   * Cria o elemento de imagem da logo.
   * @returns JSX do elemento img.
   */
  private createImageElement() {
    return <img src={this.src} alt={this.description} />
  }

  /**
   * Monta o JSX que representa o componente.
   * @returns JSX que representa o componente.
   */
  render() {
    return (
      <Host class={this.getRootCssClassMap()}>
        <div id={this.customId} class={this.getCssClassMap()}>
          {this.href && this.href.trim() !== '' ? (
            <a href={this.href} target={this.target} {...(this.target === '_blank' && { rel: 'noopener noreferrer' })}>
              {this.createImageElement()}
            </a>
          ) : (
            this.createImageElement()
          )}
        </div>
      </Host>
    )
  }
}
